// WebRTC data-channel chunk size. 64 KB (2^16) — raised from 16 KB to cut
// per-chunk msgpack/backpressure overhead ~4x. Each chunk is msgpack-wrapped
// ({payloadID,id,chunk}), so the sent message is ~64 KB + a few bytes; live
// channels negotiate maxMessageSize = 262144 (256 KB, verified), leaving ample
// headroom. Stay well under 256 KB if raising further.
const CHUNK_SIZE = Math.pow(2, 16)

let encode //encode method dependency injection
export function setEncode(newEncode) {
    encode = newEncode
}

let drawingCanvas // this is a canvas used by imageToBlob

const MAX_RECURSIVE_DEPTH = 10
//
// @param  {Function} callback []
//
export async function generateWebRTCpayload(obj) {
    // let rand = Math.random()
    // console.time(`generateWebRTCpayload-${rand}`)
    // console.time(`recursivelyEncodeBlobs-${rand}`)
    let deBlobbed = await recursivelyEncodeBlobs(obj)
    // console.timeEnd(`recursivelyEncodeBlobs-${rand}`)
    let result = _generateWebRTCpayload(deBlobbed)
    // console.timeEnd(`generateWebRTCpayload-${rand}`)
    return result
}

// Blob.arrayBuffer(), not FileReader: the same bytes in a browser, and
// FileReader does not exist in Node, where a BrowserDAV serving a directory
// answers peers over the data channel (found 2026-09-28: "sendBig error:
// FileReader is not defined" was the whole of a response never sent).
export async function deBlob(obj) {
    const view = new Int8Array(await obj.arrayBuffer())
    let descript = { isBlob: true, type: obj.type }
    if (obj.lastModified) descript.lastModified = obj.lastModified
    if (obj.name) descript.name = obj.name
    if (obj.size) descript.size = obj.size
    if (obj.exif) descript.exif = obj.exif
    descript.view = view
    return descript
}

export async function recursivelyEncodeBlobs(obj, depth = 0) {
    if (depth > MAX_RECURSIVE_DEPTH) {
        throw ('max depth reached', depth)
    }

    if (obj === undefined || obj === null) return obj

    if (
        (typeof File !== 'undefined' && obj.constructor == File) ||
        (typeof Blob !== 'undefined' && obj.constructor == Blob)
    ) {
        return await deBlob(obj)
    } else if (obj.constructor == Object) {
        let res = {}
        for (let i in obj) {
            if (obj[i] !== undefined) {
                res[i] = await recursivelyEncodeBlobs(obj[i], depth + 1)
            }
        }
        return res
    }
    return obj
}

export async function recursivelyDecodeBlobs(obj, depth = 0) {
    if (depth > MAX_RECURSIVE_DEPTH) {
        throw ('max depth reached', depth)
    }

    if (obj === undefined || obj === null) return obj

    // A blob descriptor from deBlob(): rehydrate. Keyed on the marker and
    // the payload, not on `type` — a type-less Blob (type '') used to fail
    // this test and reach the consumer as a bare descriptor object
    // (found by TransferBench's under-gate echo, webrtc-transfer-tuning).
    if (obj.constructor == Object && obj.isBlob === true && obj.view !== undefined) {
        let descript = {}
        for (let i in obj) {
            if (i !== 'view' && i !== 'chunks') {
                descript[i] = obj[i]
            }
        }
        if (typeof Blob !== 'undefined') {
            return new Blob([obj.view], descript)
        } else {
            return { view: obj.view, descript }
        }
    } else if (obj.constructor == Object) {
        let res = {}
        for (let i in obj) {
            res[i] = await recursivelyDecodeBlobs(obj[i], depth + 1)
        }
        return res
    }

    return obj
}

export async function _generateWebRTCpayload(obj, headerOpt = {}) {
    // let rand = Math.random()
    // console.time(`_generateWebRTCpayload-${rand}`)
    let bin = encode(obj)
    // console.log({ bin, obj })
    let header = Object.assign(
        {
            iAmAHeader: true,
            payloadID: Math.floor(Math.random() * 100000000),
        },
        headerOpt
    )
    let chunks = arrayBufferToChunks(bin, header.payloadID)
    header.chunkCount = chunks.length
    // console.timeEnd(`_generateWebRTCpayload-${rand}`)

    let encodedHeader = encode(header)
    // console.log(encodedHeader, header)
    return { header: encodedHeader, chunks: chunks }
}

export function arrayBufferToChunks(buff, payloadID) {
    // let rand = Math.random()

    // console.time(`chunks-${rand}`)
    let result = []
    let wholeshebang = new Uint8Array(buff)
    let count = 0
    payloadID = payloadID || Math.floor(Math.random() * 100000000)
    for (let i = 0; i < buff.byteLength; i += CHUNK_SIZE) {
        let chunksize = Math.min(buff.byteLength - i, CHUNK_SIZE)
        let chunk = wholeshebang.slice(i, i + chunksize)
        let id = count
        let chbin = encode({ payloadID: payloadID, id: id, chunk: chunk })
        result.push(chbin)
        count++
    }

    // console.log(buff, result)

    // console.timeEnd(`chunks-${rand}`)
    //console.log(`generated ${count} chunks`)
    return result
}

export function imageToBlob(img, cb) {
    if (!drawingCanvas) {
        drawingCanvas = document.createElement('canvas')
    }
    drawingCanvas.width = img.width
    drawingCanvas.height = img.height
    drawingCanvas.getContext('2d').drawImage(img, 0, 0, img.width, img.height)
    drawingCanvas.toBlob(function (blob) {
        cb(blob)
    })
}

//
// Takes a bunch of possibly out of order chunks and assembles them into one
//
export function UnChunkerFactory(options = {}) {
    const decode =
        options.decode ||
        function decode(data) {
            return data
        }

    return class UnChunker {
        constructor() {
            this.payloads = {}
            this.payloadCount = 0
            this.onData = function (val) {
                // Don't log val — raw peer payload.
                console.log('default onData: data ready (no handler set),',
                    typeof val === 'object' ? 'bytes:' : 'type:',
                    val?.byteLength ?? val?.length ?? typeof val)
            }
            // Periodic sweep to evict incomplete payloads from broken connections
            this._sweepInterval = setInterval(() => this._evictStalePayloads(), 30000)
        }

        destroy() {
            if (this._sweepInterval) {
                clearInterval(this._sweepInterval)
                this._sweepInterval = null
            }
            this.payloads = {}
            this.payloadCount = 0
        }

        _evictStalePayloads() {
            const now = Date.now()
            for (const id of Object.keys(this.payloads)) {
                const pl = this.payloads[id]
                if (now - pl.lastUpdate.getTime() > 60000) {
                    console.warn(`[unchunker] evicting stale payload ${id} (${pl.chunks.length}/${pl.count} chunks)`)
                    this._removePayload(id)
                }
            }
        }

        registerChunk(msg) {
            let header = this.parseHeader(msg)
            if (header) {
                this._newPayload(header.payloadID, header)
            } else if (this._isChunk(msg)) {
                //the msg is a chunk hopefully
                try {
                    let val = decode(msg)
                    this._appendToPayload(val)
                    if (this._isPayloadReady(val.payloadID)) {
                        this._assembleChunks(val.payloadID, (result) => {
                            this.onData(result)
                            return result
                        })
                    }
                } catch (err) {
                    // Don't dump msg — raw peer payload.
                    console.error('unchunk error:', err?.message || err)
                }
            } else {
                console.warn('not my type', decode(msg))
            }
            return null
        }

        _newPayload(id, header) {
            this.payloads[id] = Object.assign(header, {
                count: header.chunkCount,
                chunks: [],
                lastUpdate: new Date(),
                startedAt: performance.now(),
            })
            this.payloadCount++
        }

        _appendToPayload(chunk) {
            let pl = this.payloads[chunk.payloadID]
            pl.lastUpdate = new Date()
            pl.chunks.push(chunk)
        }

        async _assembleChunks(payloadID, cb) {
            let pl = this.payloads[payloadID]
            pl.chunks.sort(function (a, b) {
                return Number(a.id) - Number(b.id)
            })
            let totalSize = 0
            for (let i = 0; i < pl.chunks.length; i++) {
                totalSize += pl.chunks[i].chunk.length
            }
            let result = new Uint8Array(totalSize)
            let position = 0
            for (let i = 0; i < pl.chunks.length; i++) {
                let ch = pl.chunks[i]
                result.set(ch.chunk, position)
                position += ch.chunk.length
            }
            try {
                let val1 = decode(result)
                let val2 = await recursivelyDecodeBlobs(val1)
                // Transfer metric (binary-transfer-performance "Measuring
                // Impact"): first chunk → decoded payload. Gated on the
                // runtime debug flag; retrievable live via debug-console.
                if (globalThis.acequia?.config?.debug) {
                    const ms = performance.now() - pl.startedAt
                    console.log(
                        `[transfer] payload ${payloadID}: ${totalSize} B in ${pl.chunks.length} chunks, ${ms.toFixed(1)} ms (first-chunk→decoded)`
                    )
                }
                cb(val2)
                this._removePayload(payloadID)
            } catch (err) {
                console.error(err)
                console.error('buffer', result)
            }
        }

        _removePayload(id) {
            delete this.payloads[id]
            this.payloadCount--
        }

        parseHeader(data) {
            if (typeof data == 'object' && !(data instanceof Uint8Array)) {
                if (data.chunkCount && data.chunkCount > 0) {
                    return data
                }
            } else if (data.length && data.length < 4000) {
                // might have been packed or something.
                try {
                    let json = decode(data)
                    if (json) {
                        try {
                            if (json && json.iAmAHeader) {
                                return json
                            }
                        } catch (er) {
                            // probably not a header. Not a big deal
                        }
                    }
                } catch (er) {
                    console.warn(er)
                }
            }
            return undefined
        }

        _isChunk(msg) {
            if (this.payloadCount <= 0) {
                return false
            }
            return msg instanceof Uint8Array || msg instanceof DataView
        }

        _isPayloadReady(id) {
            let pl = this.payloads[id]
            if (pl.chunks.length == pl.count) {
                return true
            }
            return false
        }
    }
}
